<template>
  <div class="trending-content-section">
    <!-- Section Heading -->
    <div class="mb-[47px]">
      <div class="flex items-center gap-3 mb-2">
        <div class="w-[7px] h-[23px] bg-red-600"></div>
        <h2
          class="font-sans font-extrabold not-italic text-sm leading-none tracking-widest uppercase"
        >
          Trending Content
        </h2>
      </div>
    </div>

    <!-- Content Grid: 2/3 left (featured) + 1/3 right (stacked content) -->
    <div class="grid grid-cols-1 lg:grid-cols-[2fr_1fr] items-stretch gap-[44px]">
      <!-- Left Column - 2/3 width (Primary Content) -->
      <div class="trending-featured">
        <div v-if="isContentLoading" class="h-full">
          <div class="bg-neutral-200 dark:bg-neutral-800 rounded-lg h-full"></div>
        </div>
        <div
          v-else-if="contentItems && contentItems.length > 0"
          class="featured-content-card h-full"
        >
          <!-- Primary Slot - Any content type -->
          <div class="primary-slot">
            <ContentCard :content="contentItems[0]" />
          </div>
        </div>
        <div v-else class="text-center text-neutral-500 py-12">
          <div class="text-6xl mb-4">📰</div>
          <p class="text-lg">No content available</p>
        </div>
      </div>

      <!-- Right Column - 1/3 width (Secondary Content) -->
      <div class="trending-sidebar">
        <div v-if="isContentLoading" class="flex flex-col gap-10">
          <div v-for="i in 2" :key="i">
            <div class="bg-neutral-200 dark:bg-neutral-800 rounded-lg aspect-[5/4]"></div>
          </div>
        </div>
        <div v-else-if="contentItems && contentItems.length > 1" class="flex flex-col gap-10">
          <!-- Secondary Slot 1 -->
          <div v-if="contentItems[1]" class="secondary-slot">
            <ContentCard :content="contentItems[1]" />
          </div>

          <!-- Secondary Slot 2 -->
          <div v-if="contentItems[2]" class="secondary-slot">
            <ContentCard :content="contentItems[2]" />
          </div>
        </div>
        <div v-else class="text-center text-neutral-500 py-8">
          <div class="text-4xl mb-2">📄</div>
          <p class="text-lg">No additional content available</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import ContentCard from '~/components/content-card/ContentCard.vue';
import { formatDate } from '~/utils/timestamps'; // Importing shared utility

// Props for sorting behavior
const props = defineProps<{
  sort?: 'recent' | 'mixed'; // 'recent' = most recent regardless of type, 'mixed' = most recent of each type
}>();

// Fetch top 3 content items based on sort type
const { data: contentItems, isLoading } = useQuery({
  queryKey: ['trending-content', props.sort || 'mixed'],
  queryFn: async () => {
    // Fetch all content types
    const [articles, videos, podcasts] = await Promise.all([
      queryCollection('articles')
        .select('title', 'description', 'date', 'authors', 'badge', 'image', 'path')
        .all(),
      queryCollection('videos')
        .select(
          'title',
          'description',
          'createdAt',
          'badge',
          'mediaType',
          'mediaUrl',
          'image',
          'path'
        )
        .all(),
      queryCollection('podcasts')
        .select(
          'title',
          'description',
          'createdAt',
          'badge',
          'mediaType',
          'mediaUrl',
          'image',
          'path'
        )
        .all(),
    ]);

    const sortType = props.sort || 'mixed';

    if (sortType === 'recent') {
      // Sort by most recent regardless of type
      const allContent = [];

      // Add articles with normalized date field
      articles.forEach((article) => {
        allContent.push({
          ...article,
          sortDate: new Date(article.date),
        });
      });

      // Add videos with normalized date field
      videos.forEach((video) => {
        allContent.push({
          ...video,
          sortDate: new Date(video.createdAt),
        });
      });

      // Add podcasts with normalized date field
      podcasts.forEach((podcast) => {
        allContent.push({
          ...podcast,
          sortDate: new Date(podcast.createdAt),
        });
      });

      // Sort by date (most recent first) and take top 3
      return allContent.sort((a, b) => b.sortDate.getTime() - a.sortDate.getTime()).slice(0, 3);
    } else {
      // Mixed sort: most recent of each type (current behavior)
      const selectedItems = [];

      // Get most recent article
      if (articles.length > 0) {
        const sortedArticles = articles.sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        );
        selectedItems.push(sortedArticles[0]);
      }

      // Get most recent video
      if (videos.length > 0) {
        const sortedVideos = videos.sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        selectedItems.push(sortedVideos[0]);
      }

      // Get most recent podcast
      if (podcasts.length > 0) {
        const sortedPodcasts = podcasts.sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        selectedItems.push(sortedPodcasts[0]);
      }

      return selectedItems;
    }
  },
  staleTime: 5 * 60 * 1000, // 5 minutes cache
});

// Computed loading state
const isContentLoading = computed(() => isLoading.value || !contentItems.value);
</script>

<style scoped>
/*
 * NOTE: The following scoped styles related to .primary-slot, .secondary-slot
 * have been intentionally retained as custom CSS within this scoped style block. This is an
 * exception to the project's general rule of preferring Tailwind CSS classes. These styles are
 * crucial for ensuring that the content cards within the trending content grid maintain a consistent
 * height and responsive behavior, particularly to match the layout requirements of the trending players
 * component. Efforts to refactor these into pure Tailwind classes proved problematic in maintaining
 * the desired layout and responsiveness across different breakpoints.
 */

/* Slot-level aspect ratio control */
.primary-slot {
  height: 100%;
}

.primary-slot :deep([class$='-card']) {
  height: 100% !important;
}

.primary-slot :deep([class$='-card'] > a),
.primary-slot :deep([class$='-card'] > div) {
  height: 100% !important;
}

.primary-slot :deep([class$='-card'] > a > div),
.primary-slot :deep([class$='-card'] > div) {
  height: 100% !important;
  aspect-ratio: unset !important;
}

/* Mobile to lg breakpoint: 4:3 aspect ratio for all cards (consistent with testimonials) */
@media (max-width: 1023px) {
  .primary-slot :deep([class$='-card'] > a > div),
  .primary-slot :deep([class$='-card'] > div),
  .secondary-slot :deep([class$='-card'] > a > div),
  .secondary-slot :deep([class$='-card'] > div),
  .secondary-slot :deep(.podcast-card > div) {
    aspect-ratio: 4/3 !important;
  }
}

/* Desktop: secondary slots use 5:4 aspect ratio */
@media (min-width: 1024px) {
  .secondary-slot :deep([class$='-card'] > a > div),
  .secondary-slot :deep([class$='-card'] > div),
  .secondary-slot :deep(.podcast-card > div) {
    aspect-ratio: 5/4 !important;
  }
}

/* Primary slot - larger gap and padding */
.primary-slot :deep(.text-overlay) {
  gap: 1rem !important;
  padding: 2.5rem 2rem !important; /* py-10 px-8 */
}

/* Secondary slot - consistent spacing and smaller text */
.secondary-slot :deep(.text-overlay) {
  gap: 1rem !important;
}

.secondary-slot :deep(.card-title h3) {
  font-size: clamp(16px, 4.2vw, 21px) !important;
}

.secondary-slot :deep(.card-description-text) {
  font-size: clamp(12px, 3.6vw, 14.4px) !important;
}
</style>
